import { ApexAxisChartSeries, ApexOptions } from 'apexcharts'
import Chart from 'react-apexcharts'
import { useTranslation } from 'react-i18next'
import { NoData } from '@/ui/shared'
import InfoIcon from '@/assets/images/icons/InfoIcon.svg'

interface ReturnRateGraphProps {
  names: string[]
  itemsPurchased: number[]
  itemsReturned: number[]
  returnRates: number[]
  title: string
  height?: number
  inProgressIndex?: number
  inProgressNote?: string
  inProgressTooltip?: string
  rotateLabels?: boolean
}

const PURCHASED_COLOR = '#0148a5'
const RETURNED_COLOR = '#63a2f4'
const RETURN_RATE_COLOR = '#66C7F4'
const IN_PROGRESS_PURCHASED_COLOR = 'rgba(1, 72, 165, 0.35)'
const IN_PROGRESS_RETURNED_COLOR = 'rgba(99, 162, 244, 0.35)'
const ROTATED_LABELS_HEIGHT = 80
// Roughly what flat labels take, so a rotated graph keeps the same plot height as its neighbour
const FLAT_LABELS_HEIGHT = 37

const ReturnRateGraph: React.FC<ReturnRateGraphProps> = ({
  names,
  itemsPurchased,
  itemsReturned,
  returnRates,
  title,
  height = 300,
  inProgressIndex,
  inProgressNote,
  inProgressTooltip,
  rotateLabels = false
}) => {
  const { t } = useTranslation()

  // Apex infers the data format from the first series, so either every series
  // carries {x, y} points or none does
  const seriesData = (values: number[], inProgressColor?: string) => {
    if (inProgressIndex === undefined) return values
    return values.map((value, i) => ({
      x: names[i],
      y: value,
      ...(inProgressColor && i === inProgressIndex
        ? { fillColor: inProgressColor }
        : {})
    }))
  }

  const series: ApexAxisChartSeries = [
    {
      name: t('return_rate_table.items_purchased'),
      type: 'column',
      data: seriesData(itemsPurchased, IN_PROGRESS_PURCHASED_COLOR)
    },
    {
      name: t('return_rate_table.items_returned'),
      type: 'column',
      data: seriesData(itemsReturned, IN_PROGRESS_RETURNED_COLOR)
    },
    {
      name: t('return_rate_table.return_rate'),
      type: 'line',
      data: seriesData(returnRates)
    }
  ]

  const options: ApexOptions = {
    chart: {
      fontFamily: 'Roobert',
      type: 'line',
      stacked: false,
      toolbar: {
        show: false
      },
      zoom: {
        enabled: false
      },
      animations: {
        speed: 800,
        animateGradually: {
          enabled: true
        }
      }
    },
    dataLabels: {
      enabled: true,
      enabledOnSeries: [2],
      background: {
        borderWidth: 0
      },
      formatter: function (val: number) {
        return val.toFixed(0) + '%'
      }
    },
    colors: [PURCHASED_COLOR, RETURNED_COLOR, RETURN_RATE_COLOR],
    stroke: {
      width: [0, 0, 2]
    },
    // Hollow marker so the in-progress period reads as provisional on the rate line too
    markers:
      inProgressIndex === undefined
        ? {}
        : {
            discrete: [
              {
                seriesIndex: 2,
                dataPointIndex: inProgressIndex,
                fillColor: '#ffffff',
                strokeColor: RETURN_RATE_COLOR,
                size: 5
              }
            ]
          },
    tooltip: {
      followCursor: true,
      // Apex reads tooltip.x.formatter unguarded, so the key must stay absent
      ...(inProgressIndex !== undefined && inProgressTooltip
        ? {
            x: {
              // The XY data format hands the formatter an index, not the label
              formatter: (_val, opts) => {
                const index = opts?.dataPointIndex ?? -1
                const label = names[index] ?? ''
                return index === inProgressIndex
                  ? `${label} · ${inProgressTooltip}`
                  : label
              }
            }
          }
        : {})
    },
    plotOptions: {
      bar: {
        columnWidth: '15px',
        borderRadius: 1
      }
    },
    fill: {
      opacity: 1
    },
    xaxis: {
      categories: names,
      labels: {
        hideOverlappingLabels: false,
        trim: !rotateLabels,
        rotate: rotateLabels ? -45 : 0,
        rotateAlways: rotateLabels,
        // A fixed band keeps long names like "United Kingdom" whole instead of clipped
        ...(rotateLabels
          ? {
              minHeight: ROTATED_LABELS_HEIGHT,
              maxHeight: ROTATED_LABELS_HEIGHT
            }
          : {})
      },
      axisBorder: {
        show: false
      },
      axisTicks: {
        show: false
      },
      tooltip: {
        enabled: false
      }
    },
    yaxis: [
      {
        seriesName: t('return_rate_table.items_purchased'),
        labels: {
          formatter: function (val: number) {
            return val.toFixed(0)
          }
        },
        min: 0,
        max: Math.max(...itemsPurchased) * 1.1
      },
      {
        seriesName: 'Items returned',
        show: false,
        min: 0,
        max: Math.max(...itemsPurchased) * 1.1,
        labels: {
          formatter: function (val: number) {
            return val.toFixed(0)
          }
        }
      },
      {
        opposite: true,
        seriesName: 'Return rate',
        min: 0,
        max: 100,
        axisTicks: {
          show: false
        },
        axisBorder: {
          show: false
        },
        labels: {
          offsetX: -10,
          formatter: function (val: number) {
            return val.toFixed(0) + '%'
          }
        }
      }
    ],
    legend: {
      horizontalAlign: 'center',
      position: 'top'
    }
  }

  const hasEntries = names.length > 0

  // The note sits below the fixed-height plot so side-by-side graphs keep their axes aligned
  return (
    <div className="flex flex-col">
      <div
        className="flex flex-col"
        style={{
          height: `${rotateLabels ? height + ROTATED_LABELS_HEIGHT - FLAT_LABELS_HEIGHT : height}px`
        }}
      >
        <p className="ml-2 mr-1">{title}</p>
        {hasEntries ? (
          <div className="min-h-0 flex-1">
            <Chart
              type="line"
              series={series}
              options={options}
              height="100%"
              width="100%"
            />
          </div>
        ) : (
          <NoData />
        )}
      </div>
      {inProgressNote ? (
        <div className="ml-2 mr-4 mt-2 flex items-center gap-2 rounded-md bg-blue-100 px-2 py-1">
          <img src={InfoIcon} className="size-4 shrink-0" />
          <p className="text-xs">{inProgressNote}</p>
        </div>
      ) : null}
    </div>
  )
}

export default ReturnRateGraph
